import { WALL_GRID, type LayoutBody } from "./layout";

export interface PresetDefinition {
  key: string;
  name: string;
  body: LayoutBody;
}

const clock = (id: string, wall: { x: number; y: number; w: number; h: number }, tagline?: string) => ({
  id,
  type: "clock",
  version: 1,
  config: tagline === undefined ? {} : { tagline },
  wall,
});
const greeting = (id: string, wall: { x: number; y: number; w: number; h: number }) => ({ id, type: "weather", version: 1, config: { display: "greeting" }, wall });
const ticker = (id: string) => ({ id, type: "ticker", version: 1, config: {}, wall: { x: 0, y: 11, w: 12, h: 1 } });

// Starter screens. They are copied into editable household layouts (new ones
// are added for existing households too); "Restore defaults" re-applies the
// matching preset body.
export const PRESETS: PresetDefinition[] = [
  {
    key: "morning",
    name: "Morning",
    body: {
      grid: WALL_GRID,
      items: [
        clock("mo-clock", { x: 0, y: 0, w: 8, h: 2 }),
        greeting("mo-greeting", { x: 8, y: 0, w: 4, h: 2 }),
        { id: "mo-routines", type: "routines", version: 1, config: { period: "auto" }, wall: { x: 0, y: 2, w: 7, h: 7 } },
        { id: "mo-today", type: "calendar", version: 1, config: { view: "today" }, wall: { x: 7, y: 2, w: 5, h: 5 } },
        { id: "mo-wx", type: "weather", version: 1, config: { display: "conditions" }, wall: { x: 7, y: 7, w: 5, h: 4 } },
        { id: "mo-dinner", type: "meals", version: 1, config: { display: "tonight" }, wall: { x: 0, y: 9, w: 4, h: 2 } },
        { id: "mo-countdowns", type: "countdowns", version: 1, config: { max: 3 }, wall: { x: 4, y: 9, w: 3, h: 2 } },
        ticker("mo-ticker"),
      ],
    },
  },
  {
    key: "family-home",
    name: "Home",
    body: {
      grid: WALL_GRID,
      items: [
        clock("fh-clock", { x: 0, y: 0, w: 8, h: 2 }),
        greeting("fh-greeting", { x: 8, y: 0, w: 4, h: 2 }),
        { id: "fh-calendar", type: "calendar", version: 1, config: { view: "week" }, wall: { x: 0, y: 2, w: 7, h: 6 } },
        { id: "fh-radar", type: "radar", version: 1, config: {}, wall: { x: 7, y: 2, w: 5, h: 6 } },
        { id: "fh-notes", type: "family-notes", version: 1, config: {}, wall: { x: 0, y: 8, w: 3, h: 3 } },
        { id: "fh-upcoming", type: "upcoming", version: 1, config: { days: 6, skipToday: true }, wall: { x: 3, y: 8, w: 3, h: 3 } },
        { id: "fh-dinner", type: "meals", version: 1, config: { display: "tonight" }, wall: { x: 6, y: 8, w: 3, h: 3 } },
        { id: "fh-week", type: "weather", version: 1, config: { display: "week" }, wall: { x: 9, y: 8, w: 3, h: 3 } },
        ticker("fh-ticker"),
      ],
    },
  },
  {
    key: "full-calendar",
    name: "Calendar",
    body: {
      grid: WALL_GRID,
      items: [
        clock("fc-clock", { x: 0, y: 0, w: 8, h: 2 }, ""),
        greeting("fc-greeting", { x: 8, y: 0, w: 4, h: 2 }),
        { id: "fc-calendar", type: "calendar", version: 1, config: { view: "month" }, wall: { x: 0, y: 2, w: 9, h: 10 } },
        { id: "fc-upcoming", type: "upcoming", version: 1, config: { days: 13 }, wall: { x: 9, y: 2, w: 3, h: 6 } },
        { id: "fc-tasks", type: "tasks", version: 1, config: {}, wall: { x: 9, y: 8, w: 3, h: 4 } },
      ],
    },
  },
  {
    key: "weather-focus",
    name: "Weather & sky",
    body: {
      grid: WALL_GRID,
      items: [
        // The weather nerd's screen: the radar is the biggest thing on it,
        // beside it the station as an instrument panel (wind, barometer,
        // humidity, air quality, UV, rain, clouds); underneath the meteogram,
        // the week, the SPC storm outlook and the forecasters' notes.
        // (Sun lives on Outdoors, the moon on Night; Tonight's sky is in Add widget.)
        { id: "ws-radar", type: "radar", version: 1, config: { zoom: 7, showConditions: false }, wall: { x: 0, y: 0, w: 7, h: 8 } },
        clock("ws-clock", { x: 7, y: 0, w: 5, h: 1 }, ""),
        { id: "ws-now", type: "conditions-detail", version: 1, config: {}, wall: { x: 7, y: 1, w: 5, h: 7 } },
        { id: "ws-graphs", type: "forecast-graphs", version: 1, config: { hours: 36 }, wall: { x: 0, y: 8, w: 5, h: 4 } },
        { id: "ws-week", type: "weather", version: 1, config: { display: "week" }, wall: { x: 5, y: 8, w: 3, h: 4 } },
        { id: "ws-outlook", type: "storm-outlook", version: 1, config: {}, wall: { x: 8, y: 8, w: 4, h: 2 } },
        { id: "ws-afd", type: "forecast-discussion", version: 1, config: {}, wall: { x: 8, y: 10, w: 4, h: 2 } },
      ],
    },
  },
  {
    key: "outdoors",
    name: "Outdoors",
    body: {
      grid: WALL_GRID,
      items: [
        // Built around the trail map: relief, contours, trails, public land,
        // and the family's places and river gauges pinned on it. Beside it,
        // the day read through the household's outdoor interests (trail mud,
        // storm timing, legal light, solunar) and fire danger; then the
        // rivers, daylight and air; the places' forecasts underneath.
        { id: "od-out", type: "out-there", version: 1, config: {}, wall: { x: 0, y: 0, w: 3, h: 9 } },
        { id: "od-fire", type: "fire-danger", version: 1, config: {}, wall: { x: 0, y: 9, w: 3, h: 3 } },
        // The ski mountains sit under the map: new snow, the drive and chain laws, powder days.
        { id: "od-map", type: "trail-map", version: 1, config: { view: "places", ink: "theme", gauges: true }, wall: { x: 3, y: 0, w: 6, h: 7 } },
        { id: "od-mountains", type: "mountains", version: 1, config: {}, wall: { x: 3, y: 7, w: 6, h: 5 } },
        clock("od-clock", { x: 9, y: 0, w: 3, h: 1 }, ""),
        { id: "od-rivers", type: "rivers", version: 1, config: {}, wall: { x: 9, y: 1, w: 3, h: 4 } },
        { id: "od-spots", type: "outdoor-spots", version: 1, config: { periods: 2 }, wall: { x: 9, y: 5, w: 3, h: 4 } },
        { id: "od-air", type: "air-quality", version: 1, config: {}, wall: { x: 9, y: 9, w: 3, h: 3 } },
      ],
    },
  },
  {
    key: "chores",
    name: "Chores",
    body: {
      grid: WALL_GRID,
      items: [
        clock("ch-clock", { x: 0, y: 0, w: 8, h: 2 }, ""),
        greeting("ch-greeting", { x: 8, y: 0, w: 4, h: 2 }),
        { id: "ch-chart", type: "chores", version: 1, config: {}, wall: { x: 0, y: 2, w: 7, h: 10 } },
        { id: "ch-routines", type: "routines", version: 1, config: { period: "auto" }, wall: { x: 7, y: 2, w: 5, h: 6 } },
        { id: "ch-week", type: "week-summary", version: 1, config: {}, wall: { x: 7, y: 8, w: 5, h: 4 } },
      ],
    },
  },
  {
    key: "news",
    name: "News",
    body: {
      grid: WALL_GRID,
      items: [
        // A news station: the rotating top story, headlines by kind, and the
        // family's sports (Admin → News & sports picks the sources and teams).
        { id: "nw-hero", type: "news-hero", version: 1, config: {}, wall: { x: 0, y: 0, w: 8, h: 7 } },
        { id: "nw-local", type: "news-list", version: 1, config: { filter: "local", images: true }, wall: { x: 0, y: 7, w: 4, h: 5 } },
        { id: "nw-national", type: "news-list", version: 1, config: { filter: "national", images: true }, wall: { x: 4, y: 7, w: 4, h: 5 } },
        { id: "nw-f1", type: "f1", version: 1, config: {}, wall: { x: 8, y: 0, w: 4, h: 6 } },
        { id: "nw-team", type: "team", version: 1, config: { slot: 1 }, wall: { x: 8, y: 6, w: 4, h: 6 } },
      ],
    },
  },
  {
    key: "kitchen",
    name: "Kitchen",
    body: {
      grid: WALL_GRID,
      items: [
        clock("ki-clock", { x: 0, y: 0, w: 8, h: 2 }, ""),
        { id: "ki-tonight", type: "meals", version: 1, config: { display: "tonight" }, wall: { x: 8, y: 0, w: 4, h: 2 } },
        { id: "ki-meals", type: "meals", version: 1, config: { display: "week" }, wall: { x: 0, y: 2, w: 8, h: 4 } },
        { id: "ki-timers", type: "timers", version: 1, config: { sound: true }, wall: { x: 8, y: 2, w: 4, h: 4 } },
        { id: "ki-lists", type: "lists", version: 1, config: { list: "", showSuggestions: true }, wall: { x: 0, y: 6, w: 3, h: 6 } },
        { id: "ki-convert", type: "converter", version: 1, config: { mode: "volume" }, wall: { x: 3, y: 6, w: 6, h: 6 } },
        { id: "ki-season", type: "in-season", version: 1, config: { showStored: true }, wall: { x: 9, y: 6, w: 3, h: 6 } },
      ],
    },
  },
  {
    key: "photos",
    name: "Photos",
    body: {
      grid: WALL_GRID,
      items: [
        clock("ph-clock", { x: 0, y: 0, w: 8, h: 2 }),
        greeting("ph-greeting", { x: 8, y: 0, w: 4, h: 2 }),
        { id: "ph-frame", type: "photo-frame", version: 1, config: { interval: 30, shuffle: true, showCaption: true, showDate: true }, wall: { x: 0, y: 2, w: 9, h: 10 } },
        { id: "ph-day", type: "photo-day", version: 1, config: {}, wall: { x: 9, y: 2, w: 3, h: 5 } },
        { id: "ph-upcoming", type: "upcoming", version: 1, config: { days: 6 }, wall: { x: 9, y: 7, w: 3, h: 5 } },
      ],
    },
  },
  {
    key: "night",
    name: "Night",
    body: {
      grid: WALL_GRID,
      items: [
        clock("ni-clock", { x: 0, y: 1, w: 8, h: 4 }, ""),
        { id: "ni-moon", type: "moon", version: 1, config: {}, wall: { x: 8, y: 1, w: 4, h: 4 } },
        { id: "ni-tomorrow", type: "tomorrow", version: 1, config: {}, wall: { x: 0, y: 5, w: 5, h: 6 } },
        { id: "ni-bedtime", type: "nightly", version: 1, config: {}, wall: { x: 5, y: 5, w: 4, h: 6 } },
        { id: "ni-countdowns", type: "countdowns", version: 1, config: { max: 4 }, wall: { x: 9, y: 5, w: 3, h: 6 } },
      ],
    },
  },
  {
    key: "alerts",
    name: "Alerts",
    body: {
      grid: WALL_GRID,
      items: [
        // Wall displays switch here for dangerous weather (see alertDirector).
        clock("al-clock", { x: 0, y: 0, w: 5, h: 1 }, ""),
        { id: "al-alerts", type: "alerts", version: 1, config: {}, wall: { x: 0, y: 1, w: 5, h: 8 } },
        { id: "al-air", type: "air-quality", version: 1, config: {}, wall: { x: 0, y: 9, w: 5, h: 3 } },
        { id: "al-radar", type: "radar", version: 1, config: { zoom: 8, showConditions: false }, wall: { x: 5, y: 0, w: 7, h: 12 } },
      ],
    },
  },
];

export function presetByKey(key: string | null | undefined): PresetDefinition | undefined {
  return PRESETS.find((p) => p.key === key);
}
